import { useState } from "react";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../assets/Icons";
import BeneficiaryAccountViewAll from "../../../containers/Accounts/BeneficiaryAccount/BeneficiaryAccountViewAll";
import ReimbursementAccountViewAll from "../../../containers/Accounts/ReimbursementAccount/ReimbursementAccountViewAll/index.jsx";
import FieldForceAccountViewAll from "../../../containers/FieldForceAccount/FieldForceAccountViewAll";
import LargePopupRedirect from "../CommonPopups/CommonLargePopupRedirect";
import SmallPopup from "../CommonSmallPopup";
import SVGComponent from "../SVGComponent";
import { GlobalConfiguration } from "../../../utils/configurationProvider";
import TEXT from "../../../utils/globalText.js";
import { useSelector } from "react-redux";
import Loader from "../../Loader/Index.jsx";

/**
 * ActiveInactiveAssociatePopup
 *
 * A reusable component that displays associated account information
 * based on different flows (PAN, Bank, Existing, Not Found).
 * It also renders a popup with account details and allows toggling
 * between active and inactive accounts.
 *
 * @param {Object} props - Component props
 *
 * @param {"Pan"|"Bank"|"Existing"|"notFoundPan"|"notFoundBank"} props.flow
 * Determines which UI state to render:
 * - "Pan": Show PAN-based associated accounts
 * - "Bank": Show Bank-based associated accounts
 * - "Existing": Existing account found UI
 * - "notFound": No associated accounts UI
 *
 * @param {string} [props.pan]
 * PAN number used for fetching associated accounts
 *
 * @param {string} [props.ifsc]
 * IFSC code of the bank account
 *
 *  * @param {string} [props.activeAssociateId]
 * activeAssociateId used for disabling the already active associate in the popup list
 *
 *
 * @param {string} [props.bank]
 * Bank account number
 *
 * @param {"active"|"inactive"} props.accActiveInactiveState
 * Current tab state indicating whether active or inactive accounts are shown
 *
 * @param {(state: "active"|"inactive") => void} props.setAccActiveInactiveState
 * Function to update active/inactive tab state
 *
 * @param {string} props.currentAssociate
 * Currently selected associate type from dropdown
 * उदाहरण: "Field force account", "Reimbursement account"
 *
 * @param {(value: string) => void} props.setCurrentAssociate
 * Function to update selected associate type
 *
 * @param {boolean} props.openAssociatePopup
 * Controls visibility of the popup
 *
 * @param {(value: boolean) => void} props.setOpenAssociatePopup
 * Function to open/close the popup
 *
 * @param {number} props.dropDownNo
 * Index of the selected dropdown option for selecting associate type
 *
 *  @param {number} props.panLength
 * Length of the PAN number used for validation
 * @param {number} props.ifscLength
 * Length of the IFSC code used for validation
 * @returns {JSX.Element}
 * Returns the UI for associated accounts and popup view
 */

const ActiveInactiveAssociatePopup = ({
  flow,
  pan,
  ifsc,
  bank,
  accActiveInactiveState,
  setAccActiveInactiveState,
  currentAssociate,
  setCurrentAssociate,
  openAssociatePopup,
  setOpenAssociatePopup,
  activeAssociateId = "",
  dropDownNo = GlobalConfiguration.lengthCheckZero,
  currentRoute = "",
  panLength = 0,
  ifscLength = 0,
}) => {
  const {
    bankLookupLoading,
    panLookupLoading,
    panVerificationData,
    verifyBankAccountData,
  } = useSelector((state) => state.sharedResources);
  const handleDropdownChange = (val) => {
    setAccActiveInactiveState("active");
    setCurrentAssociate(val);
  };

  const handleCloseValue = () => {
    setAccActiveInactiveState("active");
    setCurrentAssociate(dropDownOptions[dropDownNo]);
  };

  const dropDownOptions = [
    ...(currentRoute === "beneAccount"
      ? ["Beneficiary account"]
      : [
          "Beneficiary account",
          "Reimbursement account",
          "Field force account",
          "Tax account",
        ]),
  ];

  const [isalertPopup, setIsalertPopup] = useState(false);
  const [flowSelectedId, setFlowSelectedId] = useState("");
  const navigate = useNavigate();

  const fullviewhandleFunction = () => {
    if (currentAssociate === "Field force account") {
      navigate(
        `/field_force_account_dashboard/field_force_account_view_all/field_force_account_detail_page/${flowSelectedId}`,
      );
    } else if (currentAssociate === "Reimbursement account") {
      navigate(
        `/reimbursement_account_dashboard/reimbursement_account_view_all/reimbursement_account_detail_page/${flowSelectedId}`,
      );
    } else if (currentAssociate === "Tax account") {
      navigate(
        `/tax_account_dashboard/tax_account_view_all/tax_account_detail_page/${flowSelectedId}`,
      );
    } else {
      navigate(
        `/beneficiary_account_dashboard/beneficiary_account_view_all/beneficiary_account_detail_page/${flowSelectedId}`,
      );
    }
    setOpenAssociatePopup(false);
    setFlowSelectedId("");
  };

  return (
    <>
      {isalertPopup && (
        <div className="fixed z-z-index-max flex items-center justify-center top-left-base left-left-base w-width-full h-height-full bg-PopUp-BackDrop bg-opacity-dark-opacity">
          <SmallPopup
            headerTitle={TEXT?.LEAVE_PAGE_WARNING_HEADER}
            headerFontWeight="semibold"
            headerFontColor="emphasis"
            textSize="font-lg"
            subtext={TEXT?.LEAVING_PAGE_WILL_EXIT_FROM_CURRENT_FLOW}
            btnOneText="Stay"
            btnOneHandleFunction={() => setIsalertPopup(false)}
            btnOneEnabledBgColor="var(--primary-bg)"
            btnOneOutLineColor="var(--text-primary)"
            btnOneEnabledTextColor="var(--text-primary)"
            btnOneIsDisabled={false}
            btnOneHoverOption="white"
            btnTwoText="Leave"
            btnTwoHandleFunction={() => {
              setIsalertPopup(false);
              fullviewhandleFunction();
            }}
            btnTwoEnabledBgColor="var(--negative-act)"
            btnTwoEnabledTextColor="var(--text-primary)"
            btnTwoIsDisabled={false}
            btnTwoDisabledBgColor="var(--text-disabled)"
            btnTwoIsLoaderNeeded={true}
            btnTwoHoverOption="black"
          />
        </div>
      )}
      {openAssociatePopup && (
        <LargePopupRedirect
          closeiconhandleFunction={() => {
            setOpenAssociatePopup(false);
            setFlowSelectedId("");

            if (flow !== "Existing") {
              setAccActiveInactiveState("active");
              setCurrentAssociate(dropDownOptions[dropDownNo]);
            }
          }}
          isActiveInactiveRequired={true}
          isDropDownRequired={true}
          dropDownValue={currentAssociate}
          handleDropdownChange={handleDropdownChange}
          isDropDownDisabled={flow === "Existing"}
          initialActiveTab={accActiveInactiveState}
          dropDownOptions={dropDownOptions}
          dropDownNo={dropDownNo}
          handleCloseValue={handleCloseValue}
          toggleActive={() => {
            setAccActiveInactiveState("active");
          }}
          toggleInactive={() => {
            setAccActiveInactiveState("inactive");
          }}
          redirectIcon={ICONS?.ADD_BUTTON_WHITE}
          headerTitle={
            flow === "Existing" ? "Existing Account" : "Associated Accounts"
          }
          headersubtext={
            flow === "Existing"
              ? "The following existing accounts are linked to the provided PAN & bank across different account classification."
              : flow === "Pan"
                ? "The following existing accounts are linked to the provided PAN across different account classification."
                : "The following existing accounts are linked to the provided bank across different account classification."
          }
        >
          {currentAssociate === "Field force account" ? (
            <FieldForceAccountViewAll
              type={"activeInactive"}
              accActiveInactiveState={accActiveInactiveState}
              activeInactivePan={pan}
              activeInactiveIfsc={ifsc}
              activeInactiveBank={bank}
              activeInactiveFlow={flow}
              setFlowSelectedId={setFlowSelectedId}
              setIsalertPopup={setIsalertPopup}
              id={activeAssociateId}
            />
          ) : currentAssociate === "Reimbursement account" ? (
            <ReimbursementAccountViewAll
              type={"activeInactive"}
              accActiveInactiveState={accActiveInactiveState}
              activeInactivePan={pan}
              activeInactiveFlow={flow}
              activeInactiveIfsc={ifsc}
              activeInactiveBank={bank}
              setFlowSelectedId={setFlowSelectedId}
              setIsalertPopup={setIsalertPopup}
              id={activeAssociateId}
            />
          ) : (
            <BeneficiaryAccountViewAll
              type={"activeInactive"}
              accActiveInactiveState={accActiveInactiveState}
              activeInactivePan={pan}
              activeInactiveFlow={flow}
              activeInactiveIfsc={ifsc}
              activeInactiveBank={bank}
              setFlowSelectedId={setFlowSelectedId}
              setIsalertPopup={setIsalertPopup}
              id={activeAssociateId}
            />
          )}
        </LargePopupRedirect>
      )}
      <div
        className={`flex gap-gap-sm justify-center items-start w-full rounded-border-radius-md bg-primary-card p-padding-md ${flow === "hideAssociate" && "hidden"} ${(flow === "Existing" || flow === "Bank" || flow === "Pan") && "cursor-pointer global-hover-white"} z-z-index-content relative`}
        onClick={() => {
          if (
            (panLookupLoading &&
              panLength === GlobalConfiguration.panLength &&
              panVerificationData?.status) ||
            (bankLookupLoading &&
              ifscLength === GlobalConfiguration.ifscLength &&
              verifyBankAccountData?.status)
          ) {
            return;
          } else if (flow === "Existing" || flow === "Bank" || flow === "Pan") {
            setOpenAssociatePopup(true);
          }
        }}
      >
        {flow === "Bank" || flow === "Pan" || flow === "Existing" ? (
          <img
            alt="alert"
            src={ICONS.ALERT}
            className=" w-icon-md h-icon-md"
          />
        ) : null}
        <div className="flex flex-col gap-gap-sm w-width-full">
          <div className="w-full h-auto font-semibold text-base text-text-primary flex justify-between items-center">
            <h1>
              {(panLookupLoading &&
                panLength === GlobalConfiguration.panLength &&
                panVerificationData?.status) ||
              (bankLookupLoading &&
                ifscLength === GlobalConfiguration.ifscLength &&
                verifyBankAccountData?.status) ? (
                "Associated Accounts"
              ) : (
                <>
                  {" "}
                  {flow === "Existing"
                    ? "Existing Account Found"
                    : flow === "notFoundPan" || flow === "notFoundBank"
                      ? "No Associated Accounts Found"
                      : flow === "Bank" || flow === "Pan"
                        ? "Associated Accounts Found"
                        : "Associated Accounts"}
                </>
              )}
            </h1>
            {(panLookupLoading &&
              panLength === GlobalConfiguration.panLength &&
              panVerificationData?.status) ||
            (bankLookupLoading &&
              ifscLength === GlobalConfiguration.ifscLength &&
              verifyBankAccountData?.status) ? (
              ""
            ) : flow === "Existing" || flow === "Bank" || flow === "Pan" ? (
              <SVGComponent
                url={ICONS.ARROW_RIGHT_WHITE}
                height={"var(--icon-sm)"} // as per devesh sir confirmation reduce the icon size
                width={"var(--icon-sm)"}
                color={"var(--text-primary)"}
              />
            ) : (
              ""
            )}
          </div>
          <div
            className={`w-full flex    ${
              (panLookupLoading &&
                panLength === GlobalConfiguration.panLength &&
                panVerificationData?.status) ||
              (bankLookupLoading &&
                ifscLength === GlobalConfiguration.ifscLength &&
                verifyBankAccountData?.status)
                ? "pt-padding-sm pb-padding-md"
                : ""
            } justify-center self-center  transition-all duration-300 ease-in-out`}
          >
            {(panLookupLoading &&
              panLength === GlobalConfiguration.panLength &&
              panVerificationData?.status) ||
            (bankLookupLoading &&
              ifscLength === GlobalConfiguration.ifscLength &&
              verifyBankAccountData?.status) ? (
              <Loader
                width={"var(--icon-lg)"}
                height={"var(--icon-lg)"}
              />
            ) : (
              <>
                {" "}
                {flow === "Existing" ? (
                  <ul className="w-full h-auto font-normal text-xs text-text-secondary list-disc pl-4  flex flex-col gap-gap-sm">
                    <li>Click here to view and manage the account.</li>
                  </ul>
                ) : flow === "Bank" || flow === "Pan" ? (
                  <ul className="w-full h-auto font-normal text-xs text-text-secondary list-disc pl-padding-md flex flex-col gap-gap-sm">
                    {flow === "Pan" ? (
                      <li>
                        This includes all active (approved, pending) and
                        inactive (rejected, archived, blocked) accounts linked
                        to the provided PAN.
                      </li>
                    ) : (
                      <li>
                        This includes all active (approved, pending) and
                        inactive (rejected, archived, blocked) accounts linked
                        to the provided bank.
                      </li>
                    )}

                    {flow === "Pan" ? (
                      <li>
                        Click here to manage them, or continue updating the PAN
                        details.
                      </li>
                    ) : (
                      <li>
                        Click here to manage them, or continue updating the bank
                        details.
                      </li>
                    )}
                  </ul>
                ) : flow === "notFoundPan" || flow === "notFoundBank" ? (
                  <p className="w-full h-auto font-normal text-xs text-text-secondary list-disc flex flex-col gap-gap-xs">
                    {flow === "notFoundPan"
                      ? "There are no existing accounts that are associated with the provided PAN."
                      : "There are no existing accounts that are associated with the provided bank."}
                  </p>
                ) : (
                  <p className="w-full h-auto font-normal text-xs text-text-secondary list-disc flex flex-col gap-gap-xs">
                    {flow === "defaultPan"
                      ? "Enter the PAN details to view associated accounts that have already been created and linked to the same PAN."
                      : "Enter the bank details to view associated accounts that have already been created and linked to the same bank."}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ActiveInactiveAssociatePopup;
